import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import later from '@breejs/later';
import type { Schedule } from 'breejs__later';


import { parseSchedule, createSchedule } from 'utils/recurring';

import DateTime from 'components/DateTime';
import RecurForm from 'components/RecurForm';
import RecurAdvanced from 'components/RecurAdvanced';
import HelpTooltip from 'components/help-tooltip';
import Select from 'components/select';
import SelectTaskGroupPopout from 'components/SelectTaskGroupPopout';
import Tooltip from 'components/tooltip';

import IconInfo from 'icons/icon-info';

import { RecursionRule, TemplateField, TemplateOptionType } from 'types';

const HELP_URL = {
    task: 'https://help.niftypm.com/en/articles/3514419-recurring-tasks',
    milestone: 'https://help.niftypm.com/en/articles/3759528-recurring-milestones'
};

interface NextRecursionsProps {
    schedule: Schedule,
    end?: Date,
    count: number
}

const NextRecursions = ({ schedule, end, count }: NextRecursionsProps) => {
    if (!schedule) {
        return null;
    }

    const nextEntries = schedule.next(count) as Date[];

    return (
        <>
            {
                nextEntries
                    .map((date, index) => (!end || date <= end) && (
                        <div key={index}>
                            { index + 1 }.&nbsp;
                            <DateTime format="fullDateTime">{ date }</DateTime>
                        </div>
                    ))
            }
        </>
    );
}

interface ResourceRepeatFormProps {
    resourceType: 'task' | 'milestone',
    projectId: string,
    rule?: RecursionRule,
    advancedVisible: boolean,
    defaultFields?: TemplateField[],
    onChange: (ruleProps: Partial<RecursionRule>) => void
}

const ResourceRepeatForm = ({ resourceType, projectId, rule, advancedVisible, defaultFields, onChange }: ResourceRepeatFormProps) => {
    const { t } = useTranslation(['recurring', 'date', 'common']);

    const [type, setType] = useState<RecursionRule['type']>(rule?.type || 'date');
    const [schedule, setSchedule] = useState(rule?.schedule);
    const [till, setTill] = useState(rule?.till ? new Date(rule?.till) : undefined);
    const [trigger, setTrigger] = useState(rule?.trigger);
    const [options, setOptions] = useState(rule?.options || []);
    const [fields, setFields] = useState(rule?.fields || defaultFields || []);
    const [optionsChanged, setOptionsChanged] = useState(false);
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const types = useMemo(() => {
        const result = [
            {
                id: 'date',
                text: t('types.date')
            }
        ];

        if (resourceType === 'task') {
            result.push(
                {
                    id: 'status',
                    text: t('types.status')
                },
                {
                    id: 'completion',
                    text: t('types.completion')
                }
            );
        }

        return result;
    }, [resourceType, t]);

    const handleRecurRuleChange = (recurRule: any) => {
        setSchedule(createSchedule(recurRule)?.schedules?.[0]);
    }

    const recurRule = useMemo(() => {
        return parseSchedule(schedule, typeof rule?.tz_offset !== 'undefined' ? ((rule?.tz_offset || 0) - (new Date().getTimezoneOffset() || 0)) : 0);
    }, [schedule, rule]);

    const scheduleObject = useMemo(() => {
        if (schedule) {
            return later.schedule({ schedules: [schedule] });
        }

        return null;
    }, [schedule]);

    const nextRecurrence = useMemo(() => {
        if (scheduleObject) {
            return scheduleObject.next(1) as Date;
        }

        return null;
    }, [scheduleObject]);

    const triggerChange = useCallback(() => {
        onChange({
            type,
            schedule,
            till,
            trigger,
            options: optionsChanged ? options : undefined,
            fields: fieldsChanged ? fields : undefined,
        });
    }, [type, schedule, till, trigger, options, fields, optionsChanged, fieldsChanged]);

    const handleOptionsChange = (options: TemplateOptionType[]) => {
        setOptions(options);
        setOptionsChanged(true);
    }

    const handleFieldsChange = (fields: TemplateField[]) => {
        setFields(fields);
        setFieldsChanged(true);
    }

    const handleTypeChange = ({ id }: { id: RecursionRule['type'] }) => setType(id);
    const handleTriggerChange = ({ id }: { id: string }) => setTrigger(id);

    useEffect(() => {
        triggerChange();
    }, [triggerChange]);

    if (type === 'date' && !schedule) {
        setSchedule(createSchedule(recurRule)?.schedules?.[0]);
    }

    return (
        <div className="resource-repeat">
            {
                advancedVisible ? (
                    <RecurAdvanced
                        resourceType={resourceType}
                        options={options}
                        fieldsOffset={fields}
                        onOptionsChange={handleOptionsChange}
                        onFieldsChange={handleFieldsChange}
                    />
                ) : (
                    <>
                        <a href={HELP_URL[resourceType]} className="resource-repeat-help" target="_blank" rel="noopener noreferrer">
                            <HelpTooltip>
                                <a href={HELP_URL[resourceType]} target="_blank" rel="noopener noreferrer">{ t('helpUrlTitle') }</a>
                            </HelpTooltip>
                        </a>

                        {
                            types.length > 1 ? (
                                <div className="resource-repeat-row">
                                    <div className="row-label">
                                        { t('Based on') }
                                    </div>

                                    <div className="row-value">
                                        <div className="row-input">
                                            <Select
                                                small={true}
                                                selected={type}
                                                options={types}
                                                onChange={handleTypeChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        }

                        {
                            type === 'status' ? (
                                <>
                                    <div className="resource-repeat-row">
                                        <div className="row-label">
                                            { t('is set to') }
                                        </div>

                                        <div className="row-value">
                                            <div className="row-input">
                                                <div className="select field-default small">
                                                    <SelectTaskGroupPopout
                                                        selectClassName="select-toggle field-input"
                                                        xAlign="right"
                                                        defaultXPosition="left"
                                                        withProject={false}
                                                        withHead={false}
                                                        projectId={projectId}
                                                        selected={trigger}
                                                        onSelect={handleTriggerChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : type === 'completion' ? (
                                <div className="resource-repeat-row">
                                    { t('When the task is marked completed') }
                                </div>
                            ) : (
                                <RecurForm
                                    value={recurRule}
                                    end={till}
                                    onChange={handleRecurRuleChange}
                                    onEndChange={setTill}
                                />
                            )
                        }

                        {
                            scheduleObject && nextRecurrence && (!till || nextRecurrence <= till) ? (
                                <div className="resource-repeat-row">
                                    <small className="dimmed-text">
                                        { t('Next recurrence') }:&nbsp;

                                        <Tooltip
                                            tooltip={<NextRecursions schedule={scheduleObject} end={till} count={5} />}>
                                            <strong><DateTime format="fullDateTime">{ nextRecurrence }</DateTime> <IconInfo style={{ lineHeight: 0, verticalAlign: 'middle', marginTop: -2 }} /></strong>
                                        </Tooltip>
                                    </small>
                                </div>
                            ) : null
                        }
                    </>
                )
            }
        </div>
    )
}

export default ResourceRepeatForm;
